<template>
  <div id="provenance-view">
    <div class="text-start py-6 provenance-section">
      <h1 class="py-2">{{ $t('provenance.title') }}</h1>
      <div class="text-body-2 white-secondary--text py-2">{{ $t('provenance.descriptionLine1') }}</div>
      <div class="text-body-2 white-secondary--text pb-2">{{ $t('provenance.descriptionLine2') }}</div>
    </div>
    <div class="text-start py-6 provenance-section">
      <h2 class="py-2 text-uppercase">{{ $t('provenance.importantInfoTitle') }}</h2>
      <div class="py-2">
        {{ $t('provenance.contractAddr') }}:
        <span class="primary--text">
          <a :href="explorerContractUri(contractAddr)" target="_blank">
            {{ contractAddr }}
            <v-icon small class="ml-2">mdi-open-in-new</v-icon>
          </a>
          <v-icon @click="$root.$emit('copy-text', contractAddr)" small class="ml-2">mdi-content-copy</v-icon>
        </span>
      </div>
      <div class="py-2">
        {{ $t('provenance.deployedTo') }}:
        <span class="primary--text">
          <v-icon small color="primary" class="pb-1">mdi-ethereum</v-icon> Ethereum Mainnet
        </span>
      </div>
      <div class="py-2">
        {{ $t('provenance.provenanceHash') }}:
        <span class="primary--text">
          {{ provenanceData.provenanceHash }}
          <v-icon @click="$root.$emit('copy-text', provenanceData.provenanceHash)" small class="ml-2">mdi-content-copy</v-icon>
        </span>
      </div>
      <div class="py-2" v-if="startingIndex !== null">
        {{ $t('provenance.finalStartingIndex') }}:
        <span class="primary--text">{{ startingIndex }}</span>
      </div>
    </div>
    <div class="text-start py-6 provenance-section">
      <h2 class="py-2 text-uppercase">
        {{ $t('provenance.concatenatedStringTitle') }}
        <v-icon @click="$root.$emit('copy-text', provenanceData.concatenatedHashes)" small class="ml-2">mdi-content-copy</v-icon>
      </h2>
      <div class="hash-string py-2">
        {{ provenanceData.concatenatedHashes }}
      </div>
    </div>
    <div class="text-start py-6 provenance-section">
      <h2 class="py-2 text-uppercase">
        {{ $t('provenance.provenanceTableTitle') }}
      </h2>
      <v-data-table
        :headers="tableHeaders"
        :items="provenanceData.provenanceTable"
        :sort-by.sync="tableSortBy"
        :sort-desc.sync="tableSortDesc"
        :items-per-page.sync="tableItemsPerPage"
        :footer-props="{
          itemsPerPageOptions: [ 10, 50, 100, 500, totalTokens]
        }"
      >
        <template v-slot:[`item.imageSha256Hash`]="{ item }">
          {{ item.imageSha256Hash| shortenHash }}
          <v-icon @click="$root.$emit('copy-text', item.imageSha256Hash)" small class="ml-2">mdi-content-copy</v-icon>
        </template>
        <template v-slot:[`item.ipfsHash`]="{ item }">
          <a :href="ipfsNftGatewayUri(item.ipfsHash)" target="_blank">
            {{ item.ipfsHash | shortenHash }}
            <v-icon small class="ml-2">mdi-open-in-new</v-icon>
          </a>
          <v-icon @click="$root.$emit('copy-text', item.ipfsHash)" small class="ml-2">mdi-content-copy</v-icon>
        </template>
      </v-data-table>
    </div>
    <div class="py-6">
    <v-btn large outlined color="primary" href="static/provenance.json">{{ $t('provenance.viewProvenanceFile') }}</v-btn>
    </div>
  </div>
</template>

<script>
import provenanceFile from '@/../public/dapp/static/provenance.json'
import { contractAddr, totalTokens, ipfsNftGatewayUri, explorerContractUri } from '@/data/constants.js'

export default {
  components: {

  },
  data () {
    return {
      tableSortBy: 'preorderIndex',
      tableSortDesc: false,
      tableItemsPerPage: 10
    }
  },
  metaInfo () {
    return {
      title: 'The QBits - ' + this.$t('nav.provenance')
    }
  },
  computed: {
    contractAddr: () => contractAddr,
    totalTokens: () => totalTokens,
    isReordered () {
      return !!provenanceFile.reorderedProvenance
    },
    provenanceData () {
      const provenanceData = this.isReordered
        ? provenanceFile.reorderedProvenance
        : provenanceFile.preorderedProvenance
      provenanceData.provenanceTable = provenanceData.provenanceTable.sort(x => x.preorderIndex)
      return provenanceData
    },
    startingIndex () {
      if (this.isReordered) {
        const startingRecord = this.provenanceData.provenanceTable.find(record => record.assignedTokenId === 0)
        if (startingRecord !== undefined) {
          return startingRecord.preorderIndex
        }
      }
      return null
    },
    tableHeaders () {
      return [
        {
          text: this.$t('provenance.provenanceTable.initialIndex'),
          class: 'text-uppercase text-md-body-1',
          value: 'preorderIndex'
        },
        this.isReordered ? {
          text: this.$t('provenance.provenanceTable.assignedId'),
          class: 'text-uppercase text-md-body-1',
          value: 'assignedTokenId'
        } : null,
        {
          text: this.$t('provenance.provenanceTable.sha256Hash'),
          class: 'text-uppercase text-md-body-1',
          value: 'imageSha256Hash',
          sortable: false
        },
        this.isReordered ? {
          text: this.$t('provenance.provenanceTable.ipfsFile'),
          class: 'text-uppercase text-md-body-1',
          value: 'ipfsHash',
          sortable: false
        } : null
      ].filter(x => x)
    }
  },
  methods: {
    ipfsNftGatewayUri,
    explorerContractUri
  },
  watch: {

  },
  mounted () {

  }
}
</script>

<style lang="scss">
  @import '@/styles/main.scss';

  #provenance-view {
    margin-bottom: 300px;
    .provenance-section {
      border-bottom: 1px solid $primary;
      font-weight: 500;
      a {
        text-decoration: none;
      }
    }
    .hash-string {
      font-size: 10px;
      line-height: 15px;
      color: $whiteSecondary;
      white-space: normal !important;
      overflow-wrap: break-word;
    }
  }

</style>
